import { hasCinderRole } from 'app/plugins/openstack/components/infrastructure/helpers'
import store from 'app/store'
import { sessionStoreKey } from 'core/session/sessionReducers'
import { userHasRole } from 'core/utils/rbac'
import type { CheckStatus, ConnectivityOutcome, OutcomeLevel, VerifyBackendCheck } from './model'

type BadgeVariant = 'success' | 'warning' | 'error' | 'unknown'

// SVCDETAILS-FR-018, SVCHEALTH-FR-009, SVCHEALTH-FR-011: who sees the check entry points.
// ponytail: interim mapping until PM confirms what "Platform Admin" means; change this list only.
const ALLOWED_ROLES = ['admin', 'platform_admin']
// The check itself has no feature flag (PCD-8795), only the role list above.
export const canRunStorageConnectivityCheck = () => userHasRole(ALLOWED_ROLES)

// STORCONN-FR-009: Volume Mappings (panel tab and configuration page tab) is on by default.
// A DU turns it off with ENABLE_VOLUME_MAPPINGS=false (top level, default true) or
// FEATURE_VOLUME_MAPPINGS=false (generic `features` block). Only a strict `false` turns it off.
export const isVolumeMappingsFlagOn = () => {
  const features = store.getState()[sessionStoreKey]?.features
  return features?.volume_mappings !== false && features?.features?.volume_mappings !== false
}
export const canSeeVolumeMappings = () =>
  isVolumeMappingsFlagOn() && canRunStorageConnectivityCheck()

// EHD-FR-009 / ENT-FR-001: Enroll Hosts (batch action and enrollment tray) is on by default.
// A DU turns it off with ENABLE_ENROLL_HOSTS=false (top level, default true) or
// FEATURE_ENROLL_HOSTS=false (generic `features` block). Only a strict `false` turns it off.
const isEnrollHostsFlagOn = () => {
  const features = store.getState()[sessionStoreKey]?.features
  return features?.enroll_hosts !== false && features?.features?.enroll_hosts !== false
}
export const canEnrollHosts = () => isEnrollHostsFlagOn() && canRunStorageConnectivityCheck()

export const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

// STORCONN-FR-002: saved outcomes are per region and per cinder-volume host@backend.
export const outcomeKey = (region: string, serviceHost: string) => `${region}|${serviceHost}`

export interface BackendService {
  serviceHost: string // os-services `host`, i.e. <resmgr host id>@<backend>
  backend: string
  hostId: string
  hostName: string
  up: boolean
}

// Rows from storageServicesSelector: enabled cinder-volume services joined to their resmgr host.
interface StorageServiceRow {
  host: string
  state?: string
  volumeBackend: string
  hostDetails?: { id: string; info?: { hostname?: string } }
}

// HV-004 / STORCONN-FR-003: the cinder-volume `host` is `<resmgr host id>@<backend>`
// (checked on a DU), so storageServicesSelector's join by host id gives each host's backends.
// Services with no matching resmgr host are dropped, like the rest of Service Health does.
export const toBackendServices = (rows: StorageServiceRow[] = []): BackendService[] =>
  rows
    .filter((row) => row.hostDetails?.id)
    .map((row) => ({
      serviceHost: row.host,
      backend: row.volumeBackend,
      hostId: row.hostDetails.id,
      hostName: row.hostDetails.info?.hostname || row.hostDetails.id,
      up: row.state === 'up',
    }))

// STORCONN-FR-004 / SVCHEALTH-FR-009: why a host can't be checked, if it can't.
export type HostStorageState = 'noRole' | 'noBackend' | 'ok'
export const hostStorageState = (
  host: { roles?: string[] } | undefined,
  backendCount: number,
): HostStorageState => {
  if (!hasCinderRole(host)) return 'noRole'
  return backendCount ? 'ok' : 'noBackend'
}

export const HOST_STATE_TEXT: Record<Exclude<HostStorageState, 'ok'>, string> = {
  noRole: "No storage role, so this host's connectivity isn't checked.",
  noBackend: "No volume backend is configured on this host, so its connectivity isn't checked.",
}

// STORCONN-FR-005: host side rows describe only the storage host that ran the check.
// iscsi_host, fc_host and nvme_host are the generic checks every driver gets.
// nfs_host_mount comes with the NetApp checks (pf9-cinder PR #250).
const HOST_SIDE_SUFFIX = /(_host_path|_unique|_host_iqn|_host_nqn)$/
const HOST_SIDE_IDS = ['iscsi_host', 'fc_host', 'nvme_host', 'nfs_host_mount']
export const isHostSideCheck = (id: string) =>
  HOST_SIDE_SUFFIX.test(id) || HOST_SIDE_IDS.includes(id)

// STORCONN-FR-005: keep returned order; unknown ids land under the array group.
export const groupChecks = (checks: VerifyBackendCheck[] = []) => ({
  array: checks.filter((check) => !isHostSideCheck(check.id)),
  host: checks.filter((check) => isHostSideCheck(check.id)),
})

export const CHECK_LABELS: Record<CheckStatus, { label: string; variant: BadgeVariant }> = {
  pass: { label: 'Passed', variant: 'success' },
  warn: { label: 'Warning', variant: 'warning' },
  fail: { label: 'Failed', variant: 'error' },
  skipped: { label: 'Skipped', variant: 'unknown' },
}

export const OUTCOME_LABELS: Record<OutcomeLevel, { label: string; variant: BadgeVariant }> = {
  pass: { label: 'Passed', variant: 'success' },
  warn: { label: 'Needs action', variant: 'warning' },
  fail: { label: 'Failed', variant: 'error' },
  unsupported: { label: 'Not supported', variant: 'unknown' },
  error: { label: "Check didn't run", variant: 'unknown' },
}

// STORCONN-FR-006: the status the server sent. A driver with no checks of its own
// (`supported: false`) still runs the generic ones, so it can pass, warn or fail too; the
// server says unsupported only when none of them could run.
export const outcomeLevel = ({ result }: ConnectivityOutcome): OutcomeLevel =>
  result ? result.status : 'error'

// SVCHEALTH-FR-010: worst first. "unsupported" is last, so it only shows when every
// saved outcome is unsupported. No outcomes means no summary.
const SUMMARY_ORDER: OutcomeLevel[] = ['fail', 'warn', 'error', 'pass', 'unsupported']
export const summarizeOutcomes = (outcomes: ConnectivityOutcome[]) => {
  const levels = outcomes.map(outcomeLevel)
  return SUMMARY_ORDER.find((level) => levels.includes(level))
}

// What ApiClient rejects with (see its response interceptor), or a plain Error.
export interface ApiError {
  response?: { status?: number; data?: unknown; headers?: Record<string, string> }
  err?: { message?: string }
  message?: string
}

// Cinder error bodies look like {"itemNotFound": {"message": "...", "code": 404}}, resmgr's
// like {"message": "..."}.
const cinderMessage = (error: ApiError) => {
  const body = error?.response?.data
  if (!body || typeof body !== 'object') return undefined
  const { message } = body as { message?: unknown }
  if (typeof message === 'string') return message
  return Object.values(body as Record<string, { message?: string }>).find((value) => value?.message)
    ?.message
}

const serverMessage = (error: ApiError) =>
  cinderMessage(error) || error?.err?.message || error?.message || 'Unknown error'

// What Cinder says when the URL itself is unknown, i.e. the API isn't on this release.
const ROUTE_NOT_FOUND = 'The resource could not be found.'

// STORCONN-FR-006: 404/406 mean the region isn't on the release with this API, a bare 504 is
// the server's 45 second cap (a 504 from Cinder itself, like "cinder-volume did not answer",
// carries its own message and is shown as is), anything else is shown with the server's
// message (volume-drift's 429 "a check is already running" included). A 404 with its own
// message (e.g. "Can't find service: host@backend") is a real answer, so it is shown as is.
export const toErrorOutcome = (error: ApiError): ConnectivityOutcome => {
  const status = error?.response?.status
  const message = cinderMessage(error)
  // LUNMAP-FR-011: storage-drift runs one check per host and a few at once (Cinder one per API
  // server) and answers 429 with Retry-After (seconds) to the next one. That's "busy", not a
  // failure; the server's message and the wait are kept.
  if (status === 429) {
    const wait = Number(error?.response?.headers?.['retry-after'])
    return {
      error: {
        kind: 'busy',
        message: serverMessage(error),
        ...(wait > 0 ? { retryAfter: wait } : {}),
      },
    }
  }
  if (status === 404 && message && message !== ROUTE_NOT_FOUND) {
    return { error: { kind: 'failed', message } }
  }
  if (status === 404 || status === 406) return { error: { kind: 'unavailable' } }
  if (status === 504 && !message) return { error: { kind: 'timeout' } }
  return { error: { kind: 'failed', message: serverMessage(error) } }
}

// "Try again in 5 seconds." from a 429's Retry-After, or a generic hint without one
export const retryText = (seconds?: number) =>
  seconds
    ? `Try again in ${seconds} ${seconds === 1 ? 'second' : 'seconds'}.`
    : 'Try again in a few seconds.'

export const errorText = (error: ConnectivityOutcome['error']) => {
  if (error?.kind === 'unavailable') return "Connectivity check isn't available in this region."
  if (error?.kind === 'busy') return `${error.message} ${retryText(error.retryAfter)}`
  if (error?.kind === 'timeout') {
    return "The storage service didn't answer within 45 seconds. Try again later."
  }
  return `Couldn't run the check: ${error?.message}`
}

// STORCONN-FR-007: plain text built only from the saved outcome. No URL, no token.
export const buildLog = (entries: { backend: string; outcome?: ConnectivityOutcome }[]) =>
  entries
    .filter(({ outcome }) => outcome)
    .map(({ backend, outcome }) => {
      const level = OUTCOME_LABELS[outcomeLevel(outcome)].label
      const { result } = outcome
      if (!result) return `${backend}: ${level}. ${errorText(outcome.error)}`
      // An older cinder-volume's reply has no check time.
      const checked = result.checked_at ? ` (checked ${result.checked_at})` : ''
      const lines = [`${backend}: ${level}. ${result.header}${checked}`, result.message]
      for (const check of result.checks ?? []) {
        lines.push(
          `  ${CHECK_LABELS[check.status]?.label ?? check.status}  ${check.header}: ${check.message}`,
        )
        if (check.remediation) lines.push(`      Fix: ${check.remediation}`)
        if (check.field) lines.push(`      Setting: ${check.field}`)
        if (check.data) lines.push(`      Data: ${JSON.stringify(check.data)}`)
        if (check.error) lines.push(`      Error: ${check.error}`)
      }
      return lines.join('\n')
    })
    .join('\n\n')
